import { Component, OnInit, ViewChild, ElementRef, Renderer2, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Store, Select, Actions, ofActionDispatched, actionMatcher } from '@ngxs/store';
import { 
  SetReady, 
  SetGraph, 
  SetEngineChapterInit,
  SynchronizeVisibleNodes,
  SetEngineChapter, 
  ClickNode, 
  FlyToNode,
  SynchronizeRenderableNodes
  } from '../../state/engine.actions';
import { EngineState } from '../../state/engine.state';
import { Observable, Subscription, fromEvent, timer, BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, debounce, takeUntil, bufferTime, debounceTime } from 'rxjs/operators';
import { EngineService, EngineEventsEnum } from '../../engine.service';
import { environment } from 'src/environments/environment';
import { EnginePositionModel } from '../../models/engine-position.model';
import { CameraService } from '../../camera.service';
import { EngineVisibilityEnum } from '../../models/engine-visibility.enum';
import { ViewTreeNode } from '../../models/view.tree-node';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { HitModel } from '../../models/hit.model';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { ChapterInitModel } from '../../models/engine-chapter-init-model';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { BdGraph, BdGraphStateMap } from 'src/lib/graph/graph.model';
import { PaintService } from '../../paint.service';
import { SetCamera, MoveCamera } from '../../state/camera/engine.camera.actions';
import { CameraMotionModel } from '../../models/camera-motion.model';
import { EngineCameraState } from '../../state/camera/engine.camera.state';
import { LoadProgressModel } from '../../models/load-progress.model';
import { AnimationService } from '../../animation.service';
import { BackgroundModel } from '../../models/background.model';
import { HoverNode } from '../../state/nodes/engine.nodes.actions';
import { EngineNodesState } from '../../state/nodes/engine.nodes.state';

import * as Hammer from 'hammerjs';
import * as _ from 'lodash';

declare const Human: any;

enum KeyboardCamera {
  Pan = 0.4, 
  PanSlow = 0.04,
  Zoom = 5,
  ZoomSlow = 1,
  Rotate = 0.3,
  RotateSlow = 0.03
}

enum MouseCamera {
  Rotate = 6,
  Pan = 1.75,
  Zoom = 2
}

enum TouchCamera {
  Zoom = -12
}

enum CursorClasses {
  Hide = 'engine--cursor-hide',
  Label = 'engine--cursor-label',
  Paint = 'engine--cursor-paint',
  Crosshair = 'engine--cursor-crosshair',
  Default = ''
}

enum MouseButtons {
  None = 0,
  Left = 1,
  Right = 2,
  LeftAndRight = 4
}

const maxMouseWheel = 42; // So long, and thanks for all the fish. Don't below 30, it's too slow.

@Component({
  selector: 'app-engine',
  templateUrl: './engine.component.html',
  styleUrls: ['./engine.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EngineComponent implements OnInit, OnDestroy {
  mouseMove$: Observable<MouseEvent>;
  
  nodeSelectedInterceptor: Subscription;

  flyCamera = true;
  chapterActivating = false;
  visible = false;

  viewGraph: BdGraph<ViewTreeNode>;
  cachedCheckedNodes: ViewTreeNode[] = [];

  cursorClass: CursorClasses = CursorClasses.Default;
  
  private mouseDelta = null;

  private ngUnsubscribe = new Subject();

  @ViewChild('engine') engineElement: ElementRef;
  @ViewChild('engineCanvas') engineCanvasElement: ElementRef;
  @ViewChild('engineAnnotations') engineAnnotationsElement: ElementRef;

  
  // Engine
  @Select(EngineState.getReady)
  ready$: Observable<boolean>;

  @Select(EngineState.getViewGraph)
  viewGraph$: Observable<BdGraph<ViewTreeNode>>;

  @Select(EngineState.getEnginePosition)
  enginePosition$: Observable<EnginePositionModel>;

  @Select(EngineNodesState.getHoveredNode)
  hoveredNode$: Observable<ViewTreeNode>;
  hoveredNode: ViewTreeNode = null;

  @Select(EngineState.getChapter)
  chapter$: Observable<string>;

  @Select(EngineState.getChapterInit)
  chapterInit$: Observable<ChapterInitModel>;

  @Select(EngineNodesState.getSelectedNode)
  selectedNode$: Observable<ViewTreeNode>;

  @Select(EngineNodesState.getCheckedNodes)
  checkedNodes$: Observable<ViewTreeNode[]>;
  
  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  @Select(EngineState.getDisplay)
  engineDisplay$: Observable<boolean>;
  display = false;

  @Select(EngineState.getVisible)
  engineVisible$: Observable<boolean>;
  engineVisible = true;
  
  // Story - Chapters
  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;


  // Camera
  @Select(EngineCameraState.getCameraMotion)
  cameraMotion$: Observable<CameraMotionModel>;

  // Load Progress
  assetProgress = 0;
  assetTotal = 0;
  loading = false;

  // Background color
  @Select(StoryChaptersState.getSelectedChapterBackground)
  background$: Observable<BackgroundModel>;

  background: BackgroundModel;

  get isTransparentBackground(): boolean {
    if (this.background) {
      return BackgroundModel.isTransparent(this.background);
    } else {
      return false;
    }
  }

  @HostListener('wheel', ['$event'])
  wheel(event: WheelEvent) {
    this.handleWheel(event);
  }

  @HostListener('contextmenu', ['$event'])
  contextmenu(event: MouseEvent) {
    this.handleContextMenu(event);
  }

  @HostListener('mouseout', ['$event'])
  mouseout() {
    this.handleMouseOut();
  }

  constructor(
    private actions: Actions,
    private animationService: AnimationService,
    private cameraService: CameraService,
    private changeDetecton: ChangeDetectorRef,
    private engineEventsService: EngineService,
    private paintService: PaintService,
    private renderer: Renderer2,
    private store: Store
  ) {
  }

  // map single property state map to graph state map
  private convertToLeafStateMap(property: string, propertyMap: {[key: string]: boolean} ): BdGraphStateMap {
    const leafStateMap: BdGraphStateMap = {};
    Object.entries(propertyMap).forEach(([id, state]) => {
      leafStateMap[id] = {};
      leafStateMap[id][property] = state;
    
    });
    return leafStateMap;
  }

  ngOnInit() {
    this.bindEngineEvents();
    this.bindApplicationEvents();
    this.bindUserEvents();
    this.bindKeyEvents();
    this.bindHumanInteractions();

    Human.properties.set({assetDomain: environment.assetDomain});
    Human.workers.setPath('/assets/engine/studio/workers');
    Human.init.init();
  }

  ngOnDestroy() {
    this.changeDetecton.detach();

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initEventListeners() {
    
  }

  bindEngineEvents() {
    // Graph has initialized
    this.engineEventsService.getEvent(EngineEventsEnum.Loaded)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.store.dispatch(new SetReady(true));
      });
      
    // Chapters have started activating
    this.engineEventsService.getEvent(EngineEventsEnum.TimelineChaptersActivateStart)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.chapterActivating = true;
      });

    // Chapters have finished
    this.engineEventsService.getEvent(EngineEventsEnum.TimelineChaptersActivateFinish)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        this.chapterActivating = false;

        const currentBranch = Human.timeline.activeRoot._nowBranch;
        const chapterInit = {
          id: currentBranch.id,
          init: true,
          camera: currentBranch.initializedCamera,      
          annotations: currentBranch.initializedAnnotations,
          graph: currentBranch.initializedGraph,
          properties: currentBranch.initializedProperties,
          particles: currentBranch.initializedParticles,
          clips: currentBranch.initializedClippingPlanes,
          postprocessing: currentBranch.initializedPostprocess,
          transparency: currentBranch.initializedTransparencyMode
        };
        this.store.dispatch(new SetEngineChapter(params.chapterId));
        this.store.dispatch(new SetEngineChapterInit(chapterInit));
      });

    // Handle engine tick, but only when it's visible
    this.engineEventsService.getEvent(EngineEventsEnum.Tick)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (this.visible) {
          this.handleMouseInputEngineTick();
          this.handleCameraTick();
        }
      });

    // Handle object visibility event
    this.engineEventsService.getEvent(EngineEventsEnum.SceneObjectsShown)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params: any) => {
        if (!this.chapterActivating) {
          const stateMap = this.convertToLeafStateMap('shown', params.enabledObjectsUpdate);
          this.store.dispatch(new SynchronizeVisibleNodes(stateMap));
        }
      });

    // Handle object renderable event
    this.engineEventsService.getEvent(EngineEventsEnum.SceneObjectRenderable)
      .pipe(
        bufferTime(1000),
        takeUntil(this.ngUnsubscribe))
      .subscribe((renderable: Array<any> ) => {
        if (renderable.length) {
          const leafStateMap = {};
          renderable.forEach(renderableData => {
            leafStateMap[renderableData.objectId] = {'renderable': true};
          });
          this.store.dispatch(new SynchronizeRenderableNodes(leafStateMap));
        }
      });

    // Handle asset load
    this.engineEventsService.getEvent(EngineEventsEnum.AssetsLoadStart)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(params => this.loading = true);

    this.engineEventsService.getEvent(EngineEventsEnum.AssetsLoadProgress)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: LoadProgressModel) => this.handleLoadProgress(result));

    this.engineEventsService.getEvent(EngineEventsEnum.AssetsLoadFinish)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(params => this.loading = false);
  }

  bindApplicationEvents() {
    // Available screen space for the engine has changed
    this.enginePosition$
      .pipe(
        filter(position => position != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((position) => {
        this.repositionEngine(position);
      });

    this.checkedNodes$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((nodes) => {
        this.paintService.highlightAll(this.cachedCheckedNodes, false);
        this.paintService.highlightAll(nodes, true);
        this.cachedCheckedNodes = [...nodes];
      });
    
    this.actions.pipe(
        ofActionDispatched(SetCamera),
        takeUntil(this.ngUnsubscribe)
      ).subscribe((action: SetCamera) => {
        this.flyToCamera(action.camera);
      });

    this.actions.pipe(
      ofActionDispatched(FlyToNode),
      filter((action: FlyToNode) => !!action.node),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((action: FlyToNode) => this.flyToNode(action.node));

    // New chapter
    this.selectedChapter$
      .pipe(
        filter((chapter) => {
          return chapter != null;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((chapter) => {
        // set background
        this.background = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);

        Human.timeline.scrub({ chapterId: chapter.id });
      });

    this.background$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((background) => {
        this.background = background;
      });
    
    // reset cached checked nodes.
    this.selectedChapter$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.cachedCheckedNodes = [];
      });
    
    // Chapter scene-graph state initialized
    this.chapterInit$
      .pipe(
        filter(chapterInit => chapterInit.graph),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => { 
        // a new chapter has had its graph updated
        const chapterGraphConfig = this.engineEventsService.getChapterGraphConfig();
        const viewGraph: BdGraph<ViewTreeNode> = this.engineEventsService.createViewGraph(chapterGraphConfig);
        this.store.dispatch(new SetGraph(viewGraph));
      });

    // Selected tool changed
    this.activeTool$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((tool) => {
        // Reset hover class
        Object.values(CursorClasses).forEach(cursorClass => {
          if (cursorClass) {
            this.renderer.removeClass(this.engineElement.nativeElement, cursorClass);
          }
        });

        switch (tool) {
          case ToolEnum.Hide:
            this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Hide);
            break;
          case ToolEnum.Label:
            this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Label);
            break;
          case ToolEnum.Paint:
            this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Paint);
            break;
          case ToolEnum.Isolate:
          case ToolEnum.Fade:
            this.renderer.addClass(this.engineElement.nativeElement, CursorClasses.Crosshair);
            break;
          default:
            // Do nothing for now
            break;
        }
      });

    // Chapter animation
    combineLatest(this.selectedChapter$, this.animationPlaying$)
      .pipe(
        filter(([chapter]) => chapter != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([chapter, playing]) => {
        const playCount = _.get(chapter, 'ui.play.count') || 0;

        if (this.animationService.isChapterAnimated(chapter) && chapter.readyState.animation && playing) {
          this.animationService.play({
            chapterId: chapter.id,
            loop: (playCount === -1),
            queue: false // True for higher playcount
          });
        }
      });

    // display
    this.engineDisplay$.subscribe((display) => {
      this.display = display;
      this.changeDetecton.markForCheck();
    });

    // visibility
    this.engineVisible$.subscribe((visible) => {
      this.engineVisible = visible;
      this.changeDetecton.markForCheck();
    });

  }
  
  bindUserEvents() {
    // User moves mouse, debounce to small delay so lookup only occurs on pause.
    // Done for performance, doing a node lookup in 3D is expensive.
    this.mouseMove$ = fromEvent<MouseEvent>(this.engineElement.nativeElement, 'mousemove');
    
    // Handle all mouse move events, primarily for panning the camera
    this.mouseMove$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((event) => {
      this.handleMousePan(event);
    });
      
      
    // User moves mouse, debounce to small delay so  hover lookup only occurs on pause.
    // Done for performance, doing a node lookup in 3D is expensive.
    this.mouseMove$.pipe(
      debounceTime(10),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((event) => { 
      this.handleMouseHover(event);
      
    });
  }

  bindKeyEvents() {
    const downKeys = ['w', 'a', 's', 'd', 'e', 'c', '=', '-', '+', '_', 'arrowleft', 'arrowright', 'arrowup', 'arrowdown', 'shiftKey']
      .reduce((result, value) => {
        result[value] = false;
        return result;
      }, {});

    const downKeys$ = new BehaviorSubject<any>(downKeys);

    this.renderer.listen(window, 'keydown', (event) => {
      if (event.repeat || event.metaKey || event.ctrlKey) {
        return;
      }
      
      // Prevent keyboard events while inputs are focused
      const eventNode = event.target.nodeName.toLowerCase();
      if (['input', 'textarea', 'select', 'option'].indexOf(eventNode) >= 0) {
        return;
      }

      downKeys$.next({
        ...downKeys$.value,
        [event.key.toLowerCase()]: true,
        shiftKey: event.shiftKey
      });
    });

    this.renderer.listen(window, 'keyup', (event: KeyboardEvent) => {
      if (event.key) {
        downKeys$.next({
          ...downKeys$.value,
          [event.key.toLowerCase()]: false,
          shiftKey: event.shiftKey
        });
      }
    });
    
    downKeys$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(value => {
        const motion = new CameraMotionModel();
        const zoom = value.shiftKey ? KeyboardCamera.ZoomSlow : KeyboardCamera.Zoom;
        const pan = value.shiftKey ? KeyboardCamera.PanSlow : KeyboardCamera.Pan;
        const rotate = value.shiftKey ? KeyboardCamera.RotateSlow : KeyboardCamera.Rotate;

        motion.zoom += (value.c || value['-'] || value['_']) ? zoom : 0;
        motion.zoom -= (value.e || value['='] || value['+']) ? zoom : 0;

        motion.panX -= value.d ? pan : 0;
        motion.panX += value.a ? pan : 0;

        motion.panY += value.w ? pan : 0;
        motion.panY -= value.s ? pan : 0;

        motion.rotateX -= value.arrowright ? rotate : 0;
        motion.rotateX += value.arrowleft ? rotate : 0;

        motion.rotateY += value.arrowup ? rotate : 0;
        motion.rotateY -= value.arrowdown ? rotate : 0;

        this.store.dispatch(new MoveCamera(motion));
    });
  }

  repositionEngine(position: EnginePositionModel) {
    this.renderer.setStyle(this.engineElement.nativeElement, 'left', `${position.left}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'top', `${position.top}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'width', `${position.width}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'height', `${position.height}px`);
    this.renderer.setStyle(this.engineElement.nativeElement, 'zIndex', `${position.visibility}`);
    this.renderer.setStyle(
      this.engineElement.nativeElement, 'visibility', (position.visibility === EngineVisibilityEnum.Shown) ? 'visible' : 'hidden'
    );

    // Engine user interaction will be disabled when the engine isn't visible
    this.visible = position.visibility === EngineVisibilityEnum.Shown;
    Human.renderer.resize();
  }

  /**
   * User Interactions
   * Depends on the HammerJS library to ensure touch compatibility, and manage pan handlers (heh...)
   */
  bindHumanInteractions() {
    // Create a HammerJS manager so we can custom define single and double click
    const mc = new Hammer.Manager(this.engineCanvasElement.nativeElement);

    // Tap recognizer with minimal 2 taps
    mc.add(new Hammer.Tap({ event: 'doubletap', taps: 2 }));

    // Single tap recognizer
    mc.add(new Hammer.Tap({ event: 'singletap' }));

    // Pan recognizer
    mc.add(new Hammer.Pan({ event: 'pan', pointers: 0 }));

    // Ping
    mc.add(new Hammer.Pinch());
    
    mc.on('singletap', (event) => {
      this.handleTap(event);
    });

    mc.on('doubletap', (event)  => {
      this.handleDoubleTap(event);
    });

    mc.on('pan', (event) => {
      event.preventDefault();
      this.handlePan(event);
    });

    let cacheScale = 0;
    mc.on('pinchstart', (event) => {
      cacheScale = event.scale;
    });

    mc.on('pinchmove', (event) => {
      const scale = event.scale - cacheScale;
      Human.renderer.camera.zoom(scale * TouchCamera.Zoom);
      cacheScale = event.scale;
    });
  }

  handleMouseHover(event: MouseEvent) {
    if (event.buttons !== MouseButtons.None) {
      return;
    }

    const { offsetX, offsetY} = event;
    const hit = Human.renderer.pick.pick(offsetX, offsetY);

    if (hit.objectId) {
      const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
      const hitNode = this.engineEventsService.getNode(viewGraph, hit.objectId);
      const hoveredNode = this.store.selectSnapshot(EngineNodesState.getHoveredNode);

      if (!hoveredNode || hoveredNode.id !== hitNode.id) {
        this.store.dispatch(new HoverNode(hitNode, hit));
      }
    } else {
      if (this.store.selectSnapshot(EngineNodesState.getHoveredNode) != null) {
        this.store.dispatch(new HoverNode(null));
      }
    }
  }

  handleMousePan(event: MouseEvent) {
    // Left mouse button is handled by HammerJS 'pan'
    if (event.buttons === 2 || event.buttons === 4) {
      let { movementX, movementY } = event;
      movementX /= 10;
      movementY /= 10;

      this.cameraService.setCameraPan(movementX, movementY);
    }
  }

  handleMouseOut() {
    this.store.dispatch(new HoverNode(null));
  }

  // When it comes to wheel events, we account for both trackpad and scroll wheel. The deltaY
  // from these events can vary wildly between trackpad, mouse type, and browser. Read the docs
  // below to learn more.
  // https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent
  handleWheel(event: WheelEvent) {
    event.preventDefault();

    let deltaY = 0;

    // Check deltaMode, which determines if we're pixel / line / page scrolling
    switch (event.deltaMode) {
      case 0: // Pixel delta (most common, trackpad and mice scroll wheels in Chrome & Safari)
        deltaY = event.deltaY;
        break;
      
      case 1: // Line delta (less common, mice scroll wheels in Firefox)
        deltaY = event.deltaY * 10;
        break;

      case 2: // Page delta (unlikely)
        deltaY = event.deltaY < 0 ? -40 : 40;
        break;
    } 

    // Some mice wheels send a deltaY of 150 or more per click, resulting in massive overzoom.
    // This caps the upper limit of that input.
    if (deltaY > maxMouseWheel) {
      deltaY = maxMouseWheel;
    } else if (deltaY < -maxMouseWheel) {
      deltaY = -maxMouseWheel;
    }

    this.cameraService.setCameraZoom(deltaY);
  }

  handleContextMenu(event: MouseEvent) {
    event.preventDefault();
    const { offsetX, offsetY} = event;
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);
  }

  handleTap(event: HammerInput) {
    const { offsetX, offsetY} = this.getOffset(event);
    
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);

    this.store.dispatch(new ClickNode(node, event, hit));
  }

  handleDoubleTap(event: HammerInput) {
    const { offsetX, offsetY} = this.getOffset(event);
    const hit = this.getHit(offsetX, offsetY);
    const node = this.getNodeFromHit(hit);

    this.store.dispatch(new FlyToNode(node));
  }
  
  handlePan(event: HammerInput) {
    const panMode = this.store.selectSnapshot(EngineCameraState.getPanMode);

    if (event.srcEvent.shiftKey || panMode === 'pan') {
      this.cameraService.setCameraPan(event.velocityX, event.velocityY * 1.5);
    } else {
      this.cameraService.setCameraRotation(event.velocityX, event.velocityY * 1.5);
    }
  }

  handleMouseInputEngineTick() {
    const rotation = this.cameraService.tickCameraRotation();
    const pan = this.cameraService.tickCameraPan();
    const zoom = this.cameraService.tickCameraZoom();

    // Rotation
    if (rotation.vx !== 0) {
      Human.renderer.camera.rotateX(rotation.vx * MouseCamera.Rotate);
    }
    
    if (rotation.vy !== 0) {
      Human.renderer.camera.rotateY(rotation.vy * MouseCamera.Rotate);
    }

    // Pan
    if (pan.x || pan.y) {
      const f = Math.max(Human.renderer.camera.lookDistance / 80, 0.01);
      Human.renderer.camera.pan({
        x: (pan.x * f) * MouseCamera.Pan,
        y: (pan.y * f) * MouseCamera.Pan
      });
    }

    // Zoom
    if (zoom !== 0) {
      Human.renderer.camera.zoom(zoom * MouseCamera.Zoom);
    }
  }

  handleCameraTick() {
    const motion = this.store.selectSnapshot(EngineCameraState.getCameraMotion);

    // PAN
    this.cameraService.setCameraPan(motion.panX, motion.panY);
    this.cameraService.setCameraRotation(motion.rotateX, motion.rotateY);
    this.cameraService.setCameraZoom(motion.zoom);
  }

  handleLoadProgress(progress: LoadProgressModel) {
    this.assetProgress = progress.receivedAssets;
    this.assetTotal = progress.requestedAssets;
    this.loading = progress.receivedAssets === progress.receivedAssets;
    this.changeDetecton.markForCheck();
  }

  flyToNode(node: ViewTreeNode) {
    const humanObjectIds = this.engineEventsService.getLeaves(node.id);
    const boundary = this.engineEventsService.getBoundaryFromObjects(humanObjectIds);
    
    Human.renderer.camera.fly.flyTo({
      boundary: boundary
    });
  }

  flyToCamera(camera) {
    Human.renderer.camera.fly.flyTo(camera);
  }

  getNodeFromHit(hit: HitModel) {
    let node: ViewTreeNode;

    if (hit.objectId) {
      const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
      node = this.engineEventsService.getNode(viewGraph, hit.objectId);
    }

    return node;
  }

  getHit(x, y) {
    // For clicks, set rayPick to true so we get a completely detailed hit object
    // that includes the exact triangle the user clicked on. This is different 
    // from hover, that only target the general node. 
    const hit = Human.renderer.pick.pick(x, y, {rayPick: true }) as HitModel;
    return hit;
  }

  getContainerStyles() {
    const styles = {};

    // set background
    if (this.background && !this.isTransparentBackground) {
      styles['background-image'] = BackgroundModel.toCSS(this.background);
    }
    // set display
    if (!this.display) {
      styles['display'] = 'none';
    }
    // set transparent
    if (!this.engineVisible) {
      styles['opacity'] = 0;
    }

    return styles;
  }

  // We calculate offsetX and offsetY using rect and center because iOS safari
  // does not natively support offsetX and offsetY. Those properties return `undefined`
  // when requested. 
  // More: http://www.jacklmoore.com/notes/mouse-position/
  getOffset(event: any): {offsetX: number, offsetY: number} {
    let x = 0;
    let y = 0;

    if (event.center) {
      x = event.center.x;
      y = event.center.y;
    } else {
      x = event.clientX;
      y = event.clientY;
    }

    const target = <HTMLDivElement>this.engineElement.nativeElement;
    const rect = target.getBoundingClientRect();
    
    return {
      offsetX: x - rect.left,
      offsetY: y - rect.top
    };
  }
}
